import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

// Redux Core Imports
import { setCurrentPage } from "../../redux/admin/homeworkSubmission.slice";
import {
  fetchSubmissionsByAssignment,
  recordSubmissionEvaluation,
} from "../../redux/admin/homeworkSubmission.thunk";
import {
  selectHomeworkSubmissions,
  selectHomeworkSubmissionLoading,
  selectHomeworkSubmissionCurrentPage,
  selectHomeworkSubmissionTotalPages,
} from "../../redux/admin/homeworkSubmission.selector";
import { fetchStudents } from "../../redux/admin/student.thunk";

import toastr from "toastr";
import "toastr/build/toastr.min.css";

/* =========================================================================
   INLINE SVG ICON COMPONENTS (Zero Dependencies)
========================================================================= */

const IconSearch = ({ size = 15, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

const IconChevronLeft = ({ size = 15, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const IconChevronRight = ({ size = 15, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const IconX = ({ size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const IconCheckCircle = ({ size = 14, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const IconInfo = ({ size = 14, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const IconTrendUp = ({ size = 14, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

const IconExternalLink = ({ size = 11, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const IconFolderOpen = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m6 14 1.45-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.55 6a2 2 0 0 1-1.94 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconLoader = ({ size = 15, className = "hms-spin" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

/* =========================================================================
   UI PRIMITIVES & HELPERS
========================================================================= */

const Skeleton = ({ w = "100%", h = 14, r = 6, style: extra = {} }) => (
  <div
    className="hms-skel"
    style={{ width: w, height: h, borderRadius: r, ...extra }}
  />
);

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const AVATAR_HUES = ["#0071e3", "#34c759", "#ff9500", "#af52de", "#ff375f", "#00c7be"];
const hueFor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_HUES[Math.abs(h) % AVATAR_HUES.length];
};

const Avatar = ({ name }) => (
  <div className="hms-avatar" style={{ background: hueFor(name) }}>
    {initials(name)}
  </div>
);

// Accuracy is only meaningful once a submission has been scored;
// tiering it gives the coach a scan-able signal without reading numbers.
const accuracyTier = (pct) => {
  if (pct == null) return "";
  if (pct >= 80) return "acc-high";
  if (pct >= 50) return "acc-mid";
  return "acc-low";
};

const formatTopic = (topic) =>
  topic ? topic.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

/* =========================================================================
   MAIN PRODUCTION COMPONENT
========================================================================= */

const HomeworkSubmissionList = () => {
  const dispatch = useDispatch();
  const limit = 10;

  /* =====================================================
     REDUX STATE & SELECTORS
  ====================================================== */
  const submissions = useSelector(selectHomeworkSubmissions) || [];
  const loading = useSelector(selectHomeworkSubmissionLoading);
  const currentPage = useSelector(selectHomeworkSubmissionCurrentPage) || 1;
  const totalPages = useSelector(selectHomeworkSubmissionTotalPages) || 1;

  const studentsState = useSelector((state) => state.admin?.students || {});
  const globalStudents = studentsState.studentList || studentsState.students || [];

  /* =====================================================
     FILTER & MODAL LOCAL STATES
  ====================================================== */
  const [activeFilter, setActiveFilter] = useState("all");
  const [topicFilter, setTopicFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("review");
  const [saving, setSaving] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [formData, setFormData] = useState({
    submission_status: "Reviewed",
    admin_notes: "",
    score: "",
    max_score: "",
    time_spent_minutes: "",
  });

  /* =====================================================
     LIFECYCLE MANAGEMENT
  ====================================================== */
  useEffect(() => {
    dispatch(
      fetchSubmissionsByAssignment({
        page: currentPage,
        limit,
        status: activeFilter !== "all" ? activeFilter : undefined,
        topic: topicFilter || undefined,
      })
    );
  }, [dispatch, currentPage, activeFilter, topicFilter]);

  useEffect(() => {
    if (globalStudents.length === 0) {
      dispatch(fetchStudents({ page: 1, limit: 1000, status: "active" }));
    }
  }, [dispatch, globalStudents.length]);

  /* =====================================================
     DATA RESOLUTIONS
  ====================================================== */
  const getStudentObject = (studentId) => {
    return globalStudents.find((s) => Number(s.id) === Number(studentId));
  };

  const getStudentName = (submission) => {
    if (submission.student_name) return submission.student_name;
    if (submission.student?.name) return submission.student.name;

    const matched = getStudentObject(submission.student_id);
    return matched
      ? `${matched.name || ""} ${matched.last_name || ""}`.trim()
      : `Student #${submission.student_id}`;
  };

  const getAdmissionNumber = (submission) => {
    if (submission.admission_number) return submission.admission_number;
    if (submission.student?.admission_number) return submission.student.admission_number;

    const matched = getStudentObject(submission.student_id);
    return matched ? matched.admission_number : "N/A";
  };

  const getStatusPillClass = (status) => {
    switch (String(status).toLowerCase()) {
      case "reviewed":
        return "pill-green";
      case "submitted":
        return "pill-blue";
      case "pending":
      default:
        return "pill-amber";
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "—";
    const dt = new Date(dateString);
    return isNaN(dt.getTime())
      ? dateString
      : dt.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  // Topics come from whatever page of results is loaded — good enough
  // for a quick filter without a dedicated topics endpoint.
  const topicOptions = useMemo(() => {
    const set = new Set(submissions.map((s) => s.topic).filter(Boolean));
    return Array.from(set).sort();
  }, [submissions]);

  /* =====================================================
     HANDLERS
  ====================================================== */
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) dispatch(setCurrentPage(page));
  };

  const handleFilterChange = (statusOption) => {
    setActiveFilter(statusOption);
    dispatch(setCurrentPage(1));
  };

  const handleTopicFilterChange = (topic) => {
    setTopicFilter(topic);
    dispatch(setCurrentPage(1));
  };

  const openEvaluationModal = (submission) => {
    setSelectedSubmission(submission);
    setFormData({
      submission_status:
        submission.submission_status === "Pending"
          ? "Reviewed"
          : submission.submission_status || "Reviewed",
      admin_notes: submission.admin_notes || submission.teacher_feedback || "",
      score: submission.score ?? "",
      max_score: submission.max_score ?? "",
      time_spent_minutes: submission.time_spent_minutes ?? "",
    });
    setActiveTab("review");
    setShowModal(true);
  };

  const handleSubmitEvaluation = async (e) => {
    e.preventDefault();
    if (!formData.submission_status) {
      toastr.warning("Please select an evaluation status.");
      return;
    }

    if (
      formData.score !== "" &&
      formData.max_score !== "" &&
      Number(formData.score) > Number(formData.max_score)
    ) {
      toastr.warning("Score can't be greater than the maximum score.");
      return;
    }

    setSaving(true);

    const submissionPayload = {
      id: selectedSubmission.id,
      evaluationData: {
        student_homework_status_id: selectedSubmission.id,
        submission_status: formData.submission_status,
        admin_notes: formData.admin_notes,
        teacher_feedback: formData.admin_notes,
        score: formData.score === "" ? null : Number(formData.score),
        max_score: formData.max_score === "" ? null : Number(formData.max_score),
        time_spent_minutes:
          formData.time_spent_minutes === "" ? null : Number(formData.time_spent_minutes),
      },
    };

    try {
      await dispatch(recordSubmissionEvaluation(submissionPayload)).unwrap();
      toastr.success("Evaluation saved successfully.");
      setShowModal(false);
      dispatch(
        fetchSubmissionsByAssignment({
          page: currentPage,
          limit,
          status: activeFilter !== "all" ? activeFilter : undefined,
          topic: topicFilter || undefined,
        })
      );
    } catch (err) {
      toastr.error(err || "Evaluation save failed.");
    } finally {
      setSaving(false);
    }
  };

  const filteredSubmissions = submissions.filter((sub) => {
    const studentName = getStudentName(sub).toLowerCase();
    const title = (sub.title || "").toLowerCase();
    const query = searchTerm.toLowerCase();
    return (
      studentName.includes(query) ||
      title.includes(query) ||
      String(sub.id).includes(query)
    );
  });

  const livePreviewAccuracy =
    formData.score !== "" && formData.max_score !== "" && Number(formData.max_score) > 0
      ? ((Number(formData.score) / Number(formData.max_score)) * 100).toFixed(1)
      : null;

  return (
    <div className="hms-page">
      {/* STICKY TRANSLUCENT HEADER */}
      <header className="hms-nav">
        <div className="hms-nav-inner">
          <div className="hms-nav-titles">
            <p className="hms-eyebrow">Academy Operations · Admin Console</p>
            <h1 className="hms-title">Homework Submissions Desk</h1>
          </div>
          <span className="hms-nav-count">
            {filteredSubmissions.length} Loaded
          </span>
        </div>
      </header>

      <div className="hms-wrap">
        {/* TOOLBAR & SEARCH */}
        <div className="hms-toolbar">
          <div className="hms-search">
            <IconSearch size={15} className="hms-search-icon" />
            <input
              type="text"
              placeholder="Search student, homework title, or ID…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {topicOptions.length > 0 && (
            <select
              className="hms-select"
              value={topicFilter}
              onChange={(e) => handleTopicFilterChange(e.target.value)}
              aria-label="Filter by topic"
            >
              <option value="">All topics</option>
              {topicOptions.map((t) => (
                <option key={t} value={t}>
                  {formatTopic(t)}
                </option>
              ))}
            </select>
          )}

          <div className="hms-filter-tabs" role="tablist">
            {[
              { key: "all", label: "All" },
              { key: "pending", label: "Pending" },
              { key: "submitted", label: "Submitted" },
              { key: "reviewed", label: "Reviewed" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeFilter === key}
                className={`hms-filter-btn${activeFilter === key ? " active" : ""}`}
                onClick={() => handleFilterChange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* DESKTOP TABLE */}
        <div className="hms-card hms-table-card">
          <div className="table-responsive">
            <table className="hms-table">
              <thead>
                <tr>
                  <th style={{ width: "6%" }}>ID</th>
                  <th style={{ width: "24%" }}>Homework Assignment</th>
                  <th style={{ width: "20%" }}>Student</th>
                  <th style={{ width: "11%" }}>Due Date</th>
                  <th style={{ width: "11%" }}>Status</th>
                  <th style={{ width: "11%" }}>Accuracy</th>
                  <th className="text-end" style={{ width: "17%" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i} className="hms-row">
                      <td>
                        <Skeleton w={32} h={12} />
                      </td>
                      <td>
                        <Skeleton w="80%" h={14} />
                        <Skeleton w="40%" h={10} style={{ marginTop: 6 }} />
                      </td>
                      <td>
                        <Skeleton w="70%" h={14} />
                        <Skeleton w="50%" h={10} style={{ marginTop: 6 }} />
                      </td>
                      <td>
                        <Skeleton w={80} h={12} />
                      </td>
                      <td>
                        <Skeleton w={74} h={22} r={20} />
                      </td>
                      <td>
                        <Skeleton w={46} h={22} r={20} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Skeleton
                          w={72}
                          h={30}
                          r={8}
                          style={{ marginLeft: "auto" }}
                        />
                      </td>
                    </tr>
                  ))
                ) : filteredSubmissions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="hms-empty-state">
                      <div className="hms-empty-icon-wrap">
                        <IconFolderOpen size={20} />
                      </div>
                      <p className="hms-empty-title">
                        No submissions match this view
                      </p>
                      <span className="hms-empty-desc">
                        Try adjusting your filter or search query.
                      </span>
                    </td>
                  </tr>
                ) : (
                  filteredSubmissions.map((sub) => (
                    <tr key={sub.id} className="hms-row">
                      <td>
                        <span className="hms-id-badge">#{sub.id}</span>
                      </td>
                      <td>
                        <div className="hms-name">
                          {sub.title || "Untitled Assignment"}
                        </div>
                        <div className="hms-subtext">
                          <span className="hms-tag">{sub.subject || "General"}</span>
                          {sub.topic && (
                            <span className="hms-tag hms-tag-topic">
                              {formatTopic(sub.topic)}
                            </span>
                          )}
                          {sub.assignment_type && (
                            <span className="text-capitalize">
                              {sub.assignment_type}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="hms-student-cell">
                          <Avatar name={getStudentName(sub)} />
                          <div>
                            <div className="hms-name">{getStudentName(sub)}</div>
                            <div className="hms-subtext">
                              {getAdmissionNumber(sub)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="hms-date-main">
                          {formatDateDisplay(sub.due_date)}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`hms-pill ${getStatusPillClass(
                            sub.submission_status
                          )}`}
                        >
                          {sub.submission_status || "Pending"}
                        </span>
                      </td>
                      <td>
                        {sub.accuracy_pct != null ? (
                          <span className={`hms-accuracy-pill ${accuracyTier(Number(sub.accuracy_pct))}`}>
                            {Number(sub.accuracy_pct).toFixed(0)}%
                          </span>
                        ) : (
                          <span className="hms-subtext">—</span>
                        )}
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="hms-btn-outline"
                          onClick={() => openEvaluationModal(sub)}
                        >
                          Evaluate
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOBILE CARD LIST */}
        <div className="hms-mobile-list">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div className="hms-mcard" key={i}>
                <Skeleton w="60%" h={14} />
                <Skeleton w="40%" h={11} style={{ marginTop: 8 }} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 14,
                  }}
                >
                  <Skeleton w={70} h={22} r={20} />
                  <Skeleton w={64} h={30} r={8} />
                </div>
              </div>
            ))
          ) : filteredSubmissions.length === 0 ? (
            <div className="hms-card hms-empty-state">
              <div className="hms-empty-icon-wrap">
                <IconFolderOpen size={20} />
              </div>
              <p className="hms-empty-title">No submissions match this view</p>
              <span className="hms-empty-desc">
                Try adjusting your filter or search query.
              </span>
            </div>
          ) : (
            filteredSubmissions.map((sub) => (
              <button
                type="button"
                className="hms-mcard"
                key={sub.id}
                onClick={() => openEvaluationModal(sub)}
              >
                <div className="hms-mcard-top">
                  <Avatar name={getStudentName(sub)} />
                  <div className="hms-mcard-titles">
                    <div className="hms-name">
                      {sub.title || "Untitled Assignment"}
                    </div>
                    <div className="hms-subtext">
                      {getStudentName(sub)} · {getAdmissionNumber(sub)}
                    </div>
                  </div>
                  <span className="hms-id-badge">#{sub.id}</span>
                </div>
                <div className="hms-mcard-bottom">
                  <span
                    className={`hms-pill ${getStatusPillClass(
                      sub.submission_status
                    )}`}
                  >
                    {sub.submission_status || "Pending"}
                  </span>
                  {sub.accuracy_pct != null && (
                    <span className={`hms-accuracy-pill ${accuracyTier(Number(sub.accuracy_pct))}`}>
                      {Number(sub.accuracy_pct).toFixed(0)}%
                    </span>
                  )}
                  <span className="hms-mcard-due">
                    Due {formatDateDisplay(sub.due_date)}
                  </span>
                  <IconChevronRight size={16} className="hms-mcard-chevron" />
                </div>
              </button>
            ))
          )}
        </div>

        {/* PAGINATION FOOTER */}
        <div className="hms-footer">
          <span className="hms-count">
            Page {currentPage} of {totalPages} ({filteredSubmissions.length} Records)
          </span>
          <div className="hms-pagination">
            <button
              type="button"
              className="hms-page-btn hms-page-arrow"
              disabled={currentPage === 1 || loading}
              onClick={() => handlePageChange(currentPage - 1)}
              aria-label="Previous page"
            >
              <IconChevronLeft size={15} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => handlePageChange(i)}
                className={`hms-page-btn${currentPage === i ? " active" : ""}`}
              >
                {i}
              </button>
            ))}

            <button
              type="button"
              className="hms-page-btn hms-page-arrow"
              disabled={currentPage === totalPages || loading}
              onClick={() => handlePageChange(currentPage + 1)}
              aria-label="Next page"
            >
              <IconChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* EVALUATION PANEL MODAL */}
      {showModal && selectedSubmission && (
        <div
          className="hms-modal-overlay"
          onClick={(e) =>
            e.target === e.currentTarget && !saving && setShowModal(false)
          }
        >
          <div className="hms-modal">
            <div className="hms-sheet-handle" />

            <div className="hms-modal-side">
              <div className="hms-modal-side-header">
                <p className="hms-modal-side-title">Evaluation Hub</p>
                <p className="hms-modal-side-sub">
                  Submission #{selectedSubmission.id}
                </p>
              </div>
              <nav className="hms-modal-nav">
                {[
                  { key: "review", Icon: IconCheckCircle, label: "Perform Review" },
                  { key: "meta", Icon: IconInfo, label: "Homework Details" },
                ].map(({ key, Icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`hms-nav-item${activeTab === key ? " active" : ""}`}
                    onClick={() => setActiveTab(key)}
                    disabled={saving}
                  >
                    <Icon size={14} />
                    <span>{label}</span>
                  </button>
                ))}
              </nav>
            </div>

            <form onSubmit={handleSubmitEvaluation} className="hms-modal-main">
              <div className="hms-modal-header">
                <div>
                  <h2 className="hms-modal-title">
                    {selectedSubmission.title || "Homework Evaluation"}
                  </h2>
                  <p className="hms-modal-sub">
                    <strong>{getStudentName(selectedSubmission)}</strong> ·{" "}
                    {getAdmissionNumber(selectedSubmission)}
                    {selectedSubmission.topic && (
                      <> · {formatTopic(selectedSubmission.topic)}</>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="hms-icon-btn"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  aria-label="Close"
                >
                  <IconX size={16} />
                </button>
              </div>

              <div className="hms-modal-body">
                {activeTab === "review" && (
                  <>
                    {selectedSubmission.student_notes && (
                      <div className="hms-note-box">
                        <label className="hms-label">Student Note / Submission</label>
                        <p className="hms-note-text">
                          {selectedSubmission.student_notes}
                        </p>
                      </div>
                    )}

                    <label className="hms-label">Target Evaluation Status *</label>
                    <div className="hms-segmented">
                      {["Pending", "Submitted", "Reviewed"].map((statusOption) => (
                        <button
                          type="button"
                          key={statusOption}
                          className={`hms-seg-btn${
                            formData.submission_status === statusOption ? " active" : ""
                          }`}
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              submission_status: statusOption,
                            }))
                          }
                        >
                          {statusOption}
                        </button>
                      ))}
                    </div>

                    {formData.submission_status === "Reviewed" && (
                      <>
                        <label className="hms-label" style={{ marginTop: 18 }}>
                          Performance Score
                        </label>
                        <div className="hms-score-row">
                          <div className="hms-score-field">
                            <span className="hms-score-field-lbl">Score</span>
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              className="hms-field"
                              placeholder="8"
                              value={formData.score}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, score: e.target.value }))
                              }
                            />
                          </div>
                          <span className="hms-score-sep">/</span>
                          <div className="hms-score-field">
                            <span className="hms-score-field-lbl">Out of</span>
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              className="hms-field"
                              placeholder="10"
                              value={formData.max_score}
                              onChange={(e) =>
                                setFormData((p) => ({ ...p, max_score: e.target.value }))
                              }
                            />
                          </div>
                          <div className="hms-score-field">
                            <span className="hms-score-field-lbl">Minutes spent</span>
                            <input
                              type="number"
                              min="0"
                              className="hms-field"
                              placeholder="20"
                              value={formData.time_spent_minutes}
                              onChange={(e) =>
                                setFormData((p) => ({
                                  ...p,
                                  time_spent_minutes: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </div>
                        {livePreviewAccuracy != null && (
                          <p className="hms-live-accuracy">
                            <IconTrendUp size={13} /> Accuracy: <strong>{livePreviewAccuracy}%</strong>
                          </p>
                        )}
                      </>
                    )}

                    <label className="hms-label" style={{ marginTop: 18 }}>
                      Administrative Notes & Teacher Feedback
                    </label>
                    <textarea
                      className="hms-field hms-textarea"
                      rows="4"
                      placeholder="Enter performance feedback or evaluation comments…"
                      value={formData.admin_notes}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          admin_notes: e.target.value,
                        }))
                      }
                    />
                  </>
                )}

                {activeTab === "meta" && (
                  <div className="hms-meta-container">
                    <div className="hms-meta-row">
                      <span className="hms-meta-lbl">Subject</span>
                      <span className="hms-meta-val">
                        {selectedSubmission.subject || "N/A"}
                      </span>
                    </div>
                    {selectedSubmission.topic && (
                      <div className="hms-meta-row">
                        <span className="hms-meta-lbl">Topic</span>
                        <span className="hms-meta-val">
                          {formatTopic(selectedSubmission.topic)}
                        </span>
                      </div>
                    )}
                    <div className="hms-meta-row">
                      <span className="hms-meta-lbl">Assignment Title</span>
                      <span className="hms-meta-val">
                        {selectedSubmission.title || "N/A"}
                      </span>
                    </div>
                    <div className="hms-meta-row">
                      <span className="hms-meta-lbl">Due Date</span>
                      <span className="hms-meta-val">
                        {formatDateDisplay(selectedSubmission.due_date)}
                      </span>
                    </div>
                    {selectedSubmission.score != null && selectedSubmission.max_score != null && (
                      <div className="hms-meta-row">
                        <span className="hms-meta-lbl">Score</span>
                        <span className="hms-meta-val">
                          {selectedSubmission.score} / {selectedSubmission.max_score}
                          {selectedSubmission.accuracy_pct != null && (
                            <span
                              className={`hms-accuracy-pill ${accuracyTier(
                                Number(selectedSubmission.accuracy_pct)
                              )}`}
                              style={{ marginLeft: 8 }}
                            >
                              {Number(selectedSubmission.accuracy_pct).toFixed(0)}%
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {selectedSubmission.time_spent_minutes != null && (
                      <div className="hms-meta-row">
                        <span className="hms-meta-lbl">Time Spent</span>
                        <span className="hms-meta-val">
                          {selectedSubmission.time_spent_minutes} min
                        </span>
                      </div>
                    )}
                    {selectedSubmission.description && (
                      <div className="hms-meta-row">
                        <span className="hms-meta-lbl">Description</span>
                        <span className="hms-meta-val hms-meta-val-wrap">
                          {selectedSubmission.description}
                        </span>
                      </div>
                    )}
                    {selectedSubmission.external_link && (
                      <div className="hms-meta-row" style={{ borderBottom: "none" }}>
                        <span className="hms-meta-lbl">External Resource</span>
                        <a
                          href={selectedSubmission.external_link}
                          target="_blank"
                          rel="noreferrer"
                          className="hms-link-url"
                        >
                          Open Resource <IconExternalLink size={11} />
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="hms-modal-footer">
                <button
                  type="button"
                  className="hms-btn-cancel"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="hms-btn-primary hms-btn-submit"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <IconLoader size={15} className="hms-spin" /> Saving…
                    </>
                  ) : (
                    "Save Evaluation"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        @keyframes hms-spin { to { transform: rotate(360deg); } }
        @keyframes hms-shimmer {
          0%   { background-position: -600px 0; }
          100% { background-position:  600px 0; }
        }
        @keyframes hms-modal-in {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);     }
        }
        @keyframes hms-sheet-in {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes hms-overlay-in { from { opacity: 0; } to { opacity: 1; } }

        :root {
          --hms-accent: #0071e3;
          --hms-accent-hover: #0077ed;
          --hms-bg: #ffffff;
          --hms-surface-alt: #f5f5f7;
          --hms-border: #e5e5ea;
          --hms-border-strong: #d2d2d7;
          --hms-text: #1d1d1f;
          --hms-text-soft: #6e6e73;
          --hms-text-faint: #aeaeb2;
        }

        * { box-sizing: border-box; }
        .hms-spin { animation: hms-spin 0.8s linear infinite; }

        .hms-page {
          background: var(--hms-bg);
          min-height: 100vh;
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", "Segoe UI", sans-serif;
          color: var(--hms-text);
          font-size: 14px;
          -webkit-font-smoothing: antialiased;
        }

        /* ---- Sticky translucent nav ---- */
        .hms-nav {
          position: sticky; top: 0; z-index: 40;
          background: rgba(255,255,255,0.78);
          backdrop-filter: saturate(180%) blur(20px);
          -webkit-backdrop-filter: saturate(180%) blur(20px);
          border-bottom: 1px solid var(--hms-border);
        }
        .hms-nav-inner {
          max-width: 1200px; margin: 0 auto; padding: 14px 24px;
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
        }
        .hms-eyebrow { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--hms-text-faint); margin: 0 0 3px; }
        .hms-title { font-size: 1.3rem; font-weight: 600; letter-spacing: -0.021em; margin: 0; }
        .hms-nav-count { font-size: 12px; color: var(--hms-text-soft); font-weight: 500; font-variant-numeric: tabular-nums; white-space: nowrap; }

        .hms-wrap { max-width: 1200px; margin: 0 auto; padding: 1.5rem 1.5rem 3rem; }

        /* ---- Toolbar ---- */
        .hms-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 1.1rem; flex-wrap: wrap; }
        .hms-search { flex: 1; min-width: 240px; position: relative; }
        .hms-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-text-faint); }
        .hms-search input {
          width: 100%; background: var(--hms-surface-alt); border: 1px solid transparent; border-radius: 10px;
          padding: 9px 12px 9px 34px; font-size: 13px; color: var(--hms-text); outline: none;
          transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
        }
        .hms-search input:focus { background: #fff; border-color: var(--hms-accent); box-shadow: 0 0 0 3px rgba(0,113,227,0.14); }

        .hms-select {
          background: var(--hms-surface-alt); border: 1px solid transparent; border-radius: 10px;
          padding: 8px 12px; font-size: 12.5px; font-weight: 500; color: var(--hms-text);
          outline: none; cursor: pointer; transition: border-color 0.15s, background 0.15s;
        }
        .hms-select:focus { background: #fff; border-color: var(--hms-accent); }

        .hms-filter-tabs { display: flex; background: var(--hms-surface-alt); border-radius: 10px; padding: 2px; gap: 2px; }
        .hms-filter-btn {
          border: none; background: transparent; padding: 7px 14px; font-size: 12.5px; font-weight: 500;
          color: var(--hms-text-soft); border-radius: 8px; cursor: pointer; transition: all 0.15s;
        }
        .hms-filter-btn:hover { color: var(--hms-text); }
        .hms-filter-btn.active { background: #fff; color: var(--hms-text); box-shadow: 0 1px 3px rgba(0,0,0,0.09); }

        .hms-btn-primary {
          background: var(--hms-accent); color: #fff; border: none; border-radius: 10px; padding: 9px 18px;
          font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
          transition: background 0.15s; white-space: nowrap; justify-content: center;
        }
        .hms-btn-primary:hover:not(:disabled) { background: var(--hms-accent-hover); }
        .hms-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .hms-btn-submit { padding: 9px 22px; min-width: 150px; }

        .hms-icon-btn {
          width: 30px; height: 30px; border-radius: 50%; border: none; background: var(--hms-surface-alt);
          display: flex; align-items: center; justify-content: center; color: var(--hms-text-soft); cursor: pointer;
          transition: background 0.15s;
        }
        .hms-icon-btn:hover:not(:disabled) { background: #e8e8ed; }

        /* ---- Desktop table ---- */
        .hms-card { background: #fff; border: 1px solid var(--hms-border-strong); border-radius: 14px; overflow: hidden; }
        .hms-table { width: 100%; border-collapse: collapse; }
        .hms-table thead th {
          background: var(--hms-surface-alt); font-size: 10.5px; font-weight: 600; color: var(--hms-text-soft);
          text-transform: uppercase; letter-spacing: 0.06em; padding: 11px 18px; text-align: left; border-bottom: 1px solid var(--hms-border);
        }
        .hms-table tbody tr { border-bottom: 1px solid var(--hms-surface-alt); transition: background 0.1s; }
        .hms-row:hover { background: #fafafa; }
        .hms-table td { padding: 13px 18px; vertical-align: middle; text-align: left; }
        .text-end { text-align: right; }

        .hms-student-cell { display: flex; align-items: center; gap: 10px; }
        .hms-avatar {
          width: 30px; height: 30px; border-radius: 50%; color: #fff; font-size: 11px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; letter-spacing: 0.02em;
        }

        .hms-id-badge { background: var(--hms-surface-alt); border: 1px solid var(--hms-border); border-radius: 6px; padding: 2px 7px; font-size: 11.5px; font-weight: 600; color: #3c3c43; font-variant-numeric: tabular-nums; }
        .hms-name { font-size: 13.5px; font-weight: 600; color: var(--hms-text); }
        .hms-subtext { font-size: 11.5px; color: var(--hms-text-faint); margin-top: 2px; display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .hms-tag { background: var(--hms-surface-alt); border: 1px solid var(--hms-border); border-radius: 5px; padding: 1px 6px; color: #3c3c43; font-weight: 500; }
        .hms-tag-topic { background: #eef2ff; border-color: #dde4fb; color: #3c4bb8; }
        .text-capitalize { text-transform: capitalize; }

        .hms-pill { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.03em; }
        .pill-amber { background: #fff4e0; color: #b8720a; border: 1px solid #ffe2b0; }
        .pill-blue  { background: #e8f2fe; color: #0058b0; border: 1px solid #cfe6fd; }
        .pill-green { background: #e8f8ec; color: #1c7a34; border: 1px solid #c9efd3; }

        .hms-accuracy-pill { display: inline-block; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .hms-accuracy-pill.acc-high { background: #e8f8ec; color: #1c7a34; }
        .hms-accuracy-pill.acc-mid  { background: #fff4e0; color: #b8720a; }
        .hms-accuracy-pill.acc-low  { background: #fdeceb; color: #c22e1f; }

        .hms-date-main { font-size: 12.5px; font-weight: 500; color: var(--hms-text); font-variant-numeric: tabular-nums; }

        .hms-btn-outline {
          border: 1px solid var(--hms-border-strong); background: #fff; border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; transition: all 0.12s;
        }
        .hms-btn-outline:hover { border-color: var(--hms-text-faint); background: var(--hms-surface-alt); color: var(--hms-text); }

        .hms-empty-state { text-align: center; padding: 3rem 1rem; }
        .hms-empty-icon-wrap { width: 48px; height: 48px; background: var(--hms-surface-alt); border-radius: 14px; display: inline-flex; align-items: center; justify-content: center; color: var(--hms-text-faint); margin-bottom: 12px; }
        .hms-empty-title { font-size: 14px; font-weight: 500; color: #3c3c43; margin: 0 0 4px; }
        .hms-empty-desc { font-size: 12px; color: var(--hms-text-faint); }

        .hms-skel {
          background: linear-gradient(90deg,#f0f0f5 25%,#e8e8ed 50%,#f0f0f5 75%);
          background-size: 600px 100%; animation: hms-shimmer 1.4s infinite linear;
        }

        /* ---- Footer / pagination ---- */
        .hms-footer { display: flex; align-items: center; justify-content: space-between; padding: 14px 4px 0; gap: 10px; flex-wrap: wrap; }
        .hms-count { font-size: 12px; color: var(--hms-text-soft); font-weight: 500; }
        .hms-pagination { display: flex; gap: 4px; }
        .hms-page-btn { min-width: 30px; height: 30px; padding: 0 6px; border: 1px solid var(--hms-border); background: #fff; border-radius: 7px; font-size: 12px; font-weight: 500; color: #3c3c43; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: all 0.12s; }
        .hms-page-btn:hover:not(:disabled):not(.active) { background: var(--hms-surface-alt); border-color: var(--hms-border-strong); }
        .hms-page-btn.active { background: var(--hms-accent); color: #fff; border-color: var(--hms-accent); }
        .hms-page-btn:disabled { opacity: 0.3; cursor: not-allowed; }

        /* ---- Mobile card list (hidden on desktop) ---- */
        .hms-mobile-list { display: none; flex-direction: column; gap: 10px; }
        .hms-mcard {
          display: block; width: 100%; text-align: left; background: #fff; border: 1px solid var(--hms-border-strong);
          border-radius: 14px; padding: 14px; cursor: pointer; transition: background 0.12s, border-color 0.12s;
        }
        .hms-mcard:active { background: var(--hms-surface-alt); }
        .hms-mcard-top { display: flex; align-items: center; gap: 10px; }
        .hms-mcard-titles { flex: 1; min-width: 0; }
        .hms-mcard-titles .hms-name { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .hms-mcard-bottom { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--hms-surface-alt); }
        .hms-mcard-due { font-size: 11.5px; color: var(--hms-text-faint); font-weight: 500; margin-left: auto; }
        .hms-mcard-chevron { color: var(--hms-text-faint); }

        /* ---- Modal / Bottom Sheet ---- */
        .hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.36); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 16px; animation: hms-overlay-in 0.2s ease; }
        .hms-modal { width: 100%; max-width: 780px; height: 560px; background: #fff; border-radius: 18px; border: 1px solid var(--hms-border-strong); box-shadow: 0 32px 60px -12px rgba(0,0,0,0.18); display: flex; overflow: hidden; animation: hms-modal-in 0.24s cubic-bezier(0.34,1.1,0.64,1); position: relative; }
        .hms-sheet-handle { display: none; }

        .hms-modal-side { width: 200px; background: var(--hms-surface-alt); border-right: 1px solid var(--hms-border); display: flex; flex-direction: column; flex-shrink: 0; }
        .hms-modal-side-header { padding: 18px 16px; border-bottom: 1px solid var(--hms-border); }
        .hms-modal-side-title { font-size: 13px; font-weight: 600; margin: 0; }
        .hms-modal-side-sub { font-size: 11px; color: var(--hms-text-faint); margin: 3px 0 0; }

        .hms-modal-nav { padding: 8px; display: flex; flex-direction: column; gap: 2px; }
        .hms-nav-item { display: flex; align-items: center; gap: 9px; padding: 9px 12px; border-radius: 9px; border: none; background: transparent; font-size: 13px; color: var(--hms-text-soft); cursor: pointer; text-align: left; width: 100%; transition: all 0.12s; }
        .hms-nav-item:hover:not(.active) { background: #ebebf0; color: #3c3c43; }
        .hms-nav-item.active { background: #fff; color: var(--hms-accent); font-weight: 500; border: 1px solid var(--hms-border); }

        .hms-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .hms-modal-header { padding: 18px 26px; border-bottom: 1px solid var(--hms-surface-alt); display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
        .hms-modal-title { font-size: 15px; font-weight: 600; margin: 0; }
        .hms-modal-sub { font-size: 12px; color: var(--hms-text-soft); margin: 3px 0 0; }
        .hms-modal-body { flex: 1; overflow-y: auto; padding: 22px 26px; }
        .hms-modal-footer { padding: 14px 26px; border-top: 1px solid var(--hms-surface-alt); display: flex; justify-content: flex-end; align-items: center; gap: 10px; background: var(--hms-surface-alt); }

        .hms-note-box { background: var(--hms-surface-alt); border: 1px solid var(--hms-border); border-radius: 10px; padding: 12px 14px; margin-bottom: 18px; }
        .hms-note-text { margin: 4px 0 0; font-size: 12.5px; color: #3c3c43; }

        .hms-label { display: block; font-size: 11px; font-weight: 600; color: var(--hms-text-soft); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
        .hms-field { width: 100%; border: 1px solid var(--hms-border-strong); border-radius: 10px; padding: 9px 12px; font-size: 13px; color: var(--hms-text); background: #fff; outline: none; transition: all 0.15s; font-family: inherit; }
        .hms-field:focus { border-color: var(--hms-accent); box-shadow: 0 0 0 3px rgba(0,113,227,0.14); }
        .hms-textarea { resize: none; }

        .hms-segmented { display: flex; background: var(--hms-surface-alt); border-radius: 10px; padding: 3px; gap: 3px; }
        .hms-seg-btn { flex: 1; border: none; background: transparent; padding: 8px 10px; font-size: 12.5px; font-weight: 500; color: var(--hms-text-soft); border-radius: 8px; cursor: pointer; transition: all 0.15s; }
        .hms-seg-btn.active { background: #fff; color: var(--hms-text); box-shadow: 0 1px 3px rgba(0,0,0,0.09); }

        .hms-score-row { display: flex; align-items: flex-end; gap: 10px; }
        .hms-score-field { flex: 1; display: flex; flex-direction: column; gap: 5px; }
        .hms-score-field-lbl { font-size: 10.5px; font-weight: 500; color: var(--hms-text-faint); }
        .hms-score-sep { padding-bottom: 9px; font-weight: 600; color: var(--hms-text-faint); }
        .hms-live-accuracy { display: flex; align-items: center; gap: 6px; margin: 10px 0 0; font-size: 12.5px; color: var(--hms-text-soft); }
        .hms-live-accuracy strong { color: var(--hms-text); }

        .hms-meta-container { background: var(--hms-surface-alt); border: 1px solid var(--hms-border); border-radius: 10px; padding: 4px 16px; }
        .hms-meta-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid var(--hms-border); gap: 10px; }
        .hms-meta-lbl { font-size: 12px; font-weight: 500; color: var(--hms-text-soft); flex-shrink: 0; }
        .hms-meta-val { font-size: 12.5px; font-weight: 600; color: var(--hms-text); text-align: right; }
        .hms-meta-val-wrap { text-align: right; max-width: 60%; font-weight: 500; }
        .hms-link-url { color: var(--hms-accent); text-decoration: none; font-weight: 500; display: inline-flex; align-items: center; gap: 5px; font-size: 12.5px; }
        .hms-link-url:hover { text-decoration: underline; }

        .hms-btn-cancel { border: 1px solid var(--hms-border-strong); background: #fff; border-radius: 9px; padding: 9px 18px; font-size: 13px; font-weight: 500; color: var(--hms-text-soft); cursor: pointer; transition: all 0.12s; }
        .hms-btn-cancel:hover { background: var(--hms-surface-alt); color: #3c3c43; }

        /* =========================================================
           MOBILE BREAKPOINT — Bottom sheet modal + card list
        ========================================================= */
        @media (max-width: 768px) {
          .hms-nav-inner { padding: 12px 18px; }
          .hms-title { font-size: 1.15rem; }
          .hms-wrap { padding: 1rem 1rem 2.5rem; }
          .hms-toolbar { flex-direction: column; align-items: stretch; }
          .hms-search { width: 100%; min-width: 0; }
          .hms-select { width: 100%; }
          .hms-filter-tabs { width: 100%; }
          .hms-filter-btn { flex: 1; text-align: center; font-size: 11.5px; padding: 8px 4px; }

          .hms-table-card { display: none; }
          .hms-mobile-list { display: flex; }

          .hms-modal-overlay { padding: 0; align-items: flex-end; }
          .hms-modal {
            flex-direction: column; height: 88vh; max-height: 88vh; width: 100%; max-width: 100%;
            border-radius: 20px 20px 0 0; border: none; border-bottom: none;
            animation: hms-sheet-in 0.28s cubic-bezier(0.32,0.72,0,1);
          }
          .hms-sheet-handle { display: block; position: absolute; top: 8px; left: 50%; transform: translateX(-50%); width: 36px; height: 4px; border-radius: 99px; background: var(--hms-border-strong); z-index: 2; }
          .hms-modal-side { width: 100%; border-right: none; border-bottom: 1px solid var(--hms-border); padding-top: 6px; }
          .hms-modal-side-header { display: none; }
          .hms-modal-nav { flex-direction: row; overflow-x: auto; padding: 8px 12px; gap: 6px; }
          .hms-nav-item { white-space: nowrap; width: auto; font-size: 12.5px; padding: 8px 14px; }
          .hms-modal-body { padding: 16px 18px; }
          .hms-modal-header { padding: 14px 18px; }
          .hms-modal-footer { padding: 12px 18px calc(12px + env(safe-area-inset-bottom)); background: #fff; border-top: 1px solid var(--hms-border); }
          .hms-btn-cancel { flex: 1; }
          .hms-btn-submit { flex: 2; }

          .hms-score-row { flex-wrap: wrap; }
          .hms-score-sep { display: none; }
        }

        @media (prefers-reduced-motion: reduce) {
          .hms-modal, .hms-spin, .hms-skel { animation: none !important; }
        }
      `}</style>
    </div>
  );
};

export default HomeworkSubmissionList;
